import { Component, EventEmitter, inject, OnDestroy, OnInit, Output } from '@angular/core';
import { LoopModeEnum } from '@campus/ui';
import {
  BehaviorSubject,
  concatMap,
  delay,
  from,
  map,
  Observable,
  of,
  repeat,
  startWith,
  Subject,
  takeUntil,
} from 'rxjs';
import {
  GuideStoryComponentEnum,
  GuideStoryInterface,
  kabasStudentAnimationSteps,
  polpoStudentAnimationSteps,
  story,
  teacherAnimationSteps,
} from './result-guide-story.config';

import { EnvironmentPlatformType, ENVIRONMENT_PLATFORM_TOKEN } from '@campus/environment';
import '@diekeure/dcr-components/dcr-checkbox.js';
import '@diekeure/dcr-components/dcr-circular-score.js';
import '@diekeure/dcr-components/dcr-dialog.js';
import '@diekeure/dcr-components/dcr-filled-button.js';
import '@diekeure/dcr-components/dcr-icon.js';
import '@diekeure/dcr-components/dcr-outline-icon-button.js';

@Component({
  standalone: false,
  selector: 'campus-result-guide',
  templateUrl: './result-guide.component.html',
  styleUrls: ['./result-guide.component.scss'],
})
export class ResultGuideComponent implements OnInit, OnDestroy {
  public GuideStoryComponent = GuideStoryComponentEnum;
  public LoopMode = LoopModeEnum;

  public rememberHide: boolean;
  public totalStorySteps: number;

  public isDialogOpen$: BehaviorSubject<boolean>;
  public currentStep$: BehaviorSubject<number>;
  public currentStoryForCurrentStep$: Observable<GuideStoryInterface>;

  public currentTeacherAnimationProgress$: Observable<number>;
  public currentPolpoStudentAnimationProgress$: Observable<number>;
  public currentKabasStudentAnimationProgress$: Observable<{ value: number; mode: LoopModeEnum }>;

  public destroyed$: Subject<boolean> = new Subject<boolean>();

  @Output() dialogClosed = new EventEmitter<boolean>();

  public platform: EnvironmentPlatformType = inject(ENVIRONMENT_PLATFORM_TOKEN);

  public ngOnInit(): void {
    this.rememberHide = false;
    this.totalStorySteps = Object.keys(story).length;

    this.initStreams();
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public nextStep(): void {
    this.currentStep$.next(this.currentStep$.value + 1);
  }

  public previousStep(): void {
    this.currentStep$.next(this.currentStep$.value - 1);
  }

  public onDialogClosedEvent(): void {
    this.dialogClosed.emit(this.rememberHide);
  }

  public onAcknowledgeClickEvent(): void {
    this.isDialogOpen$.next(false);
  }

  public onRememberHideChangeEvent(event: any): void {
    this.rememberHide = event.target.checked;
  }

  private initStreams(): void {
    this.currentStep$ = new BehaviorSubject<number>(1);
    this.isDialogOpen$ = new BehaviorSubject<boolean>(true);
    this.currentStoryForCurrentStep$ = this.getCurrentStoryForCurrentStep$();

    this.currentTeacherAnimationProgress$ = this.getCurrentTeacherAnimationProgress$();
    this.currentPolpoStudentAnimationProgress$ =
      this.getCurrentStudentAnimationProgress$<number>(polpoStudentAnimationSteps);
    this.currentKabasStudentAnimationProgress$ = this.getCurrentStudentAnimationProgress$<{
      value: number;
      mode: LoopModeEnum;
    }>(kabasStudentAnimationSteps);
  }

  private getCurrentStoryForCurrentStep$(): Observable<GuideStoryInterface> {
    return this.currentStep$.pipe(map((step) => story[step]));
  }

  private getCurrentTeacherAnimationProgress$(): Observable<number> {
    return from(teacherAnimationSteps).pipe(
      takeUntil(this.destroyed$),
      concatMap((step) =>
        of(step).pipe(
          delay(500),
          map((v) => v)
        )
      ),
      repeat({ delay: 1000 }),
      startWith(teacherAnimationSteps[0])
    );
  }

  private getCurrentStudentAnimationProgress$<T>(animationSteps: any): Observable<T> {
    return from(animationSteps).pipe(
      takeUntil(this.destroyed$),
      concatMap((step) =>
        of(step).pipe(
          delay(500),
          map((v) => v)
        )
      ),
      repeat({ delay: 1000 }),
      startWith(animationSteps[0])
    );
  }
}
